// ============================================================
// authPage.js — Páginas de autenticación
// ------------------------------------------------------------
// - Toggle mostrar/ocultar contraseña ([data-auth-eye])
// - Indicador de fuerza de contraseña en registro
// - Flujo de recuperación: envía email → muestra pantalla de
//   confirmación. El backend solo tiene que hacer POST a
//   /api/auth/recover; la UI cambia sola al recibir 200 OK.
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';
import { loginUser, registerUser, recoverPassword, loginSocial, resetPassword } from './api.js';

const AUTH_PAGES = ['login', 'registro', 'recuperar'];
const GOOGLE_CLIENT_ID = '108281880488-emar5ss7tf35uqpg4bmvrporomf9lkdn.apps.googleusercontent.com';

export function initAuthPage() {
  const page = document.body.dataset.page;
  if (!AUTH_PAGES.includes(page)) return;

  // 1. Toggle de mostrar/ocultar contraseña
  on(document, 'click', '[data-auth-eye]', (e, btn) => {
    const wrap = btn.closest('.auth-input-wrap');
    const input = wrap?.querySelector('input');
    if (!input) return;
    const isPass = input.type === 'password';
    input.type = isPass ? 'text' : 'password';
    btn.querySelector('i')?.classList.toggle('bi-eye', !isPass);
    btn.querySelector('i')?.classList.toggle('bi-eye-slash', isPass);
  });

  // 2. Indicador de fuerza de la contraseña (solo registro)
  if (page === 'registro') {
    const pass = qs('#regPass');
    const strength = qs('#passStrength');
    if (pass && strength) {
      const label = strength.querySelector('.auth-strength-label');
      pass.addEventListener('input', () => {
        const score = scorePassword(pass.value);
        strength.classList.remove('is-weak', 'is-medium', 'is-strong');
        if (!pass.value) { if (label) label.textContent = ''; return; }
        if (score < 2) { strength.classList.add('is-weak');   if (label) label.textContent = 'Débil'; }
        else if (score < 4) { strength.classList.add('is-medium'); if (label) label.textContent = 'Media'; }
        else { strength.classList.add('is-strong'); if (label) label.textContent = 'Fuerte'; }
      });
    }
  }

  // 3. Flujo de recuperación de contraseña
  if (page === 'recuperar') {
    // Detección de token en URL para cambiar contraseña
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    const resetEmail = urlParams.get('email');

    if (resetToken && resetEmail) {
      const card = qs('[data-recover-card]');
      if (card) {
        card.innerHTML = `
          <div class="auth-card-state">
            <div class="auth-card-header">
              <h1 class="auth-card-title">Restablecer contraseña</h1>
              <p class="auth-card-sub">Introduce tu nueva contraseña para acceder a la cuenta.</p>
            </div>
            <form class="auth-form" data-auth-reset-submit>
              <input type="hidden" name="email" value="${resetEmail}">
              <input type="hidden" name="token" value="${resetToken}">
              <div class="auth-field">
                <label class="auth-label" for="newPassword">Nueva contraseña</label>
                <div class="auth-input-wrap">
                  <i class="bi bi-lock"></i>
                  <input class="auth-input" type="password" id="newPassword" name="password"
                    placeholder="Mínimo 6 caracteres" required>
                </div>
              </div>
              <button class="auth-submit mk-cta-btn" type="submit">
                Cambiar contraseña <i class="bi bi-arrow-right"></i>
              </button>
            </form>
          </div>
        `;

        qs('[data-auth-reset-submit]')?.addEventListener('submit', async (ev) => {
          ev.preventDefault();
          const pass = qs('input[name="password"]', ev.target)?.value;
          if (!pass || pass.length < 6) {
            toast({ message: 'La contraseña debe tener al menos 6 caracteres.', type: 'error' });
            return;
          }
          try {
            toast({ message: 'Actualizando contraseña...', type: 'success' });
            await resetPassword(resetEmail, resetToken, pass);
            toast({ message: '¡Contraseña cambiada con éxito!', type: 'success' });
            setTimeout(() => { window.location.href = 'login.html'; }, 1200);
          } catch (err) {
            toast({ message: err.message || 'Error al cambiar contraseña.', type: 'error' });
          }
        });
      }
    } else {
      const form = qs('[data-auth-recover]');
      form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = form.querySelector('input[name="email"]')?.value?.trim();
        if (!email) return;

        try {
          const res = await recoverPassword(email);
          showRecoverSentState();
          
          // Si hay link de debug (desarrollo), mostramos un banner
          if (res.debug && res.debug.debugResetLink) {
            const container = qs('[data-recover-state="sent"]');
            if (container) {
              const debugBanner = document.createElement('div');
              debugBanner.style.marginTop = '1.5rem';
              debugBanner.style.padding = '1rem';
              debugBanner.style.background = 'rgba(234, 0, 245, 0.1)';
              debugBanner.style.border = '1px dashed #ea00f5';
              debugBanner.style.borderRadius = '8px';
              debugBanner.style.textAlign = 'center';
              debugBanner.innerHTML = `
                <p style="color: #ea00f5; font-size: 0.85rem; margin-bottom: 0.5rem; font-weight: bold;">
                  🔧 [Modo Desarrollo] Enlace de recuperación generado:
                </p>
                <a href="${res.debug.debugResetLink}" class="btn-vimo btn-vimo-sm" style="background: #ea00f5; color: white; border: none; padding: 0.4rem 0.8rem; border-radius: 4px; text-decoration: none; font-size: 0.8rem; display: inline-block;">
                  Simular clic en el enlace
                </a>
              `;
              container.appendChild(debugBanner);
            }
          }
        } catch (err) {
          toast({ message: err.message || 'No pudimos enviar el email. Inténtalo en un momento.', type: 'error' });
        }
      });

      // "Volver a enviar" en la pantalla de éxito
      on(document, 'click', '[data-recover-resend]', async () => {
        const email = qs('[data-auth-recover] input[name="email"]')?.value?.trim();
        if (email) {
          try {
            await recoverPassword(email);
            toast({ message: 'Hemos vuelto a enviar el enlace a tu email.', type: 'success' });
          } catch (err) {
            toast({ message: 'Error al volver a enviar.', type: 'error' });
          }
        }
      });
    }
  }

  // 4. Submit real del login y registro
  qs('[data-auth-login]')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = qs('input[name="email"]', e.target)?.value?.trim();
    const password = qs('input[name="password"]', e.target)?.value;

    if (!email || !password) {
      toast({ message: 'Por favor, rellena todos los campos.', type: 'error' });
      return;
    }

    try {
      toast({ message: 'Iniciando sesión…', type: 'success' });
      await loginUser(email, password);
      setTimeout(() => { window.location.href = 'escritorio.html'; }, 600);
    } catch (err) {
      toast({ message: err.message || 'Error al iniciar sesión.', type: 'error' });
    }
  });

  qs('[data-auth-register]')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = qs('input[name="email"]', e.target)?.value?.trim();
    const password = qs('input[name="password"]', e.target)?.value;

    if (!email || !password) {
      toast({ message: 'Por favor, rellena todos los campos.', type: 'error' });
      return;
    }

    try {
      toast({ message: 'Creando cuenta…', type: 'success' });
      await registerUser(email, password);
      toast({ message: 'Cuenta creada. ¡Bienvenido a Vimo!', type: 'success' });
      setTimeout(() => { window.location.href = 'escritorio.html'; }, 800);
    } catch (err) {
      toast({ message: err.message || 'Error al crear la cuenta.', type: 'error' });
    }
  });

  // 5. Autenticación social con Google (GSI One-Tap + OAuth2 Popup)
  initGoogleAuth();

  on(document, 'click', '[data-auth-social]', async (e, btn) => {
    const provider = btn.getAttribute('data-auth-social');
    
    if (provider === 'google') {
      const client = getGoogleTokenClient();
      if (client) {
        client.requestAccessToken({ prompt: 'select_account' });
      } else if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.prompt();
      } else {
        toast({ message: 'Cargando servicios de Google, por favor inténtalo en un momento…', type: 'info' });
      }
      return;
    }

    // Otros proveedores (Facebook simulado)
    toast({ message: `Conectando con ${provider}...`, type: 'info' });
    try {
      const email = `${provider}.demo@vimo.ai`;
      const name = `${provider} Demo User`;
      await loginSocial({ provider, email, name });
      toast({ message: `¡Autenticado con éxito vía ${provider}!`, type: 'success' });
      setTimeout(() => { window.location.href = 'escritorio.html'; }, 600);
    } catch (err) {
      toast({ message: err.message || 'Error en la autenticación social.', type: 'error' });
    }
  });
}

/**
 * Instancia del cliente OAuth2 de Google para popup en clic.
 */
let googleTokenClientInstance = null;

function getGoogleTokenClient() {
  if (googleTokenClientInstance) return googleTokenClientInstance;
  if (window.google?.accounts?.oauth2) {
    googleTokenClientInstance = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'email profile openid',
      callback: async (tokenResponse) => {
        if (tokenResponse.error) {
          if (tokenResponse.error !== 'popup_closed_by_user') {
            console.error('[Google OAuth Error]', tokenResponse);
            toast({ message: 'Error en la autorización con Google.', type: 'error' });
          }
          return;
        }

        if (!tokenResponse.access_token) {
          toast({ message: 'No se recibió token de acceso de Google.', type: 'error' });
          return;
        }

        toast({ message: 'Autenticando con Google…', type: 'info' });
        try {
          const res = await loginSocial({
            provider: 'google',
            token: tokenResponse.access_token,
          });

          toast({ message: `¡Bienvenido, ${res.user?.name || 'Usuario'}!`, type: 'success' });
          setTimeout(() => {
            window.location.href = 'escritorio.html';
          }, 600);
        } catch (err) {
          console.error('[Google Backend Auth Error]', err);
          toast({ message: err.message || 'Error al iniciar sesión con Google.', type: 'error' });
        }
      },
    });
  }
  return googleTokenClientInstance;
}

/**
 * Inicialización de Google Identity Services (GSI) One-Tap.
 */
function initGoogleAuth() {
  const setup = () => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Intentar mostrar One-Tap al cargar la página
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Silencioso
        }
      });
    }
  };

  if (window.google?.accounts?.id) {
    setup();
  } else {
    window.addEventListener('load', () => {
      setTimeout(setup, 300);
    });
  }
}

/**
 * Callback ejecutado cuando el usuario completa la autenticación en Google (vía One-Tap o Credential).
 */
async function handleGoogleCredentialResponse(response) {
  if (!response || !response.credential) {
    toast({ message: 'No se recibieron credenciales de Google.', type: 'error' });
    return;
  }

  toast({ message: 'Iniciando sesión con Google…', type: 'info' });
  try {
    const res = await loginSocial({
      provider: 'google',
      token: response.credential,
    });

    toast({ message: `¡Bienvenido, ${res.user?.name || 'Usuario'}!`, type: 'success' });
    setTimeout(() => {
      window.location.href = 'escritorio.html';
    }, 600);
  } catch (err) {
    console.error('[Google Auth Error]', err);
    toast({ message: err.message || 'Error al iniciar sesión con Google.', type: 'error' });
  }
}

// ── Helpers ──────────────────────────────────────────────────
function scorePassword(value) {
  let score = 0;
  if (value.length >= 8)  score++;
  if (value.length >= 12) score++;
  if (/[A-Z]/.test(value))  score++;
  if (/[0-9]/.test(value))  score++;
  if (/[^A-Za-z0-9]/.test(value)) score++;
  return score;
}

function showRecoverSentState() {
  const formState = qs('[data-recover-state="form"]');
  const sentState = qs('[data-recover-state="sent"]');
  if (!formState || !sentState) return;
  formState.hidden = true;
  sentState.hidden = false;
  sentState.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
